import {getInitialValues, TransportFormProps, TransportFormRawValues, TransportFormType} from "./TransportForm";
import {useTranslation} from "react-i18next";
import {useForm} from "@mantine/form";
import {isStringUndefinedOrBlank, transportType} from "@ots-frontend/ots-data";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {MutableRefObject, useEffect, useMemo, useRef} from "react";
import {isEqual} from "lodash";
import {
  CommodityGroupCrudeOil,
  Density,
  DensityUnitApi,
  getDensityAndVolumeAtBase,
  Pressure,
  PressureUnitPsig,
  Temperature,
  TemperatureUnitCelsius,
  TemperatureUnitFahrenheit
} from "@ots-frontend/api-mpms";
import {BigNumber, bignumber, divide, multiply, subtract} from "mathjs";

export type UseTransportFormProps = Pick<TransportFormProps, 'type' | 'defaultValues'> & {

}

export type UseTransportFormResult = {
  form: TransportFormType,
  previousValues: MutableRefObject<TransportFormRawValues | undefined>,
}

export function useTransportForm(props: UseTransportFormProps): UseTransportFormResult {
  const {
    type,
    defaultValues
  } = props

  const {t} = useTranslation(["common"])

  const {uoms} = useApp()

  const previousValues = useRef<TransportFormRawValues | undefined>(undefined)

  const pickup = useMemo(() => {
    return type === transportType.pickup
  }, [type])

  const form = useForm({
    initialValues: getInitialValues({
      ...defaultValues,
      odometer: {
        number: defaultValues?.odometer?.number,
        units: defaultValues?.odometer?.units ?? uoms?.odometer,
      },
      quantity: {
        number: defaultValues?.quantity?.number,
        units: defaultValues?.quantity?.units ?? uoms?.volume,
      },
      observedTemperature: {
        number: defaultValues?.observedTemperature?.number,
        units: defaultValues?.observedTemperature?.units ?? uoms?.temperature,
      },
      meterReading: {
        ...defaultValues?.meterReading,
        units: defaultValues?.meterReading?.units ?? uoms?.meter,
      },
    }),
    validate: {
      date: (value) => value ? undefined : t("common.validation.required", "Required"),
      person: (value) => value ? undefined : t("common.validation.required", "Required"),
      trailer: (value) => value ? undefined : t("common.validation.required", "Required"),
      freightType: (value) => value ? undefined : t("common.validation.required", "Required"),
      quantity: (value) => typeof value !== "undefined" ? undefined : t("common.validation.required", "Required"),
      quantityUom: (value) => value ? undefined : t("common.validation.required", "Required"),
      odometerUom: (value, values) => isStringUndefinedOrBlank(value) && values.odometer ? t("common.validation.required", "Required") : undefined,
      observedGravity: (value, values) => !pickup || values.category !== "OIL" || typeof value !== "undefined" ? undefined : t("common.validation.required", "Required"),
      bswPercentage: (value, values) => !pickup || values.category !== "OIL" || typeof value !== "undefined" ? undefined : t("common.validation.required", "Required"),
      observedTemperature: (value, values) => !pickup || values.category !== "OIL" || typeof value !== "undefined" ? undefined : t("common.validation.required", "Required"),
      temperatureUom: (value, values) => !pickup || values.category !== "OIL" || value ? undefined : t("common.validation.required", "Required"),
      correctedGravity: (value, values) => !pickup || values.category !== "OIL" || typeof value !== "undefined" ? undefined : t("common.validation.required", "Required"),
      netQuantity: (value, values) => !pickup || values.category !== "OIL" || typeof value !== "undefined" ? undefined : t("common.validation.required", "Required"),
      rejectionComment: (value, values) => isStringUndefinedOrBlank(value) && !isStringUndefinedOrBlank(values.rejectionType) ? t("common.validation.required", "Required") : undefined,
      site: (value) => value ? undefined : t("common.validation.required", "Required"),
    },
  })

  useEffect(() => {
    const values = form.values

    let calculate = false

    if (!isEqual(values.observedGravity, previousValues.current?.observedGravity)) {
      calculate = true
    }

    if (!isEqual(values.observedTemperature, previousValues.current?.observedTemperature)) {
      calculate = true
    }

    if (!isEqual(values.temperatureUom, previousValues.current?.temperatureUom)) {
      calculate = true
    }

    if (!isEqual(values.bswPercentage, previousValues.current?.bswPercentage)) {
      calculate = true
    }

    if (!isEqual(values.quantity, previousValues.current?.quantity)) {
      calculate = true
    }

    if (calculate) {
      const gravity = values.observedGravity
      const temp = values.observedTemperature
      const tempUom = values.temperatureUom
      const bswPercent = values.bswPercentage
      const quantity = values.quantity

      let correctedGravity: number | undefined = undefined
      let netQuantity: number | undefined = undefined

      if (typeof gravity !== "undefined" && typeof temp !== "undefined" && tempUom) {
        const obsPressure = new Pressure(0.0, PressureUnitPsig)
        const obsDensity = new Density(gravity, DensityUnitApi)

        const obsTempUnit = "celsius" === tempUom.toLowerCase() ? TemperatureUnitCelsius : TemperatureUnitFahrenheit
        const obsTemp = new Temperature(temp, obsTempUnit)

        try {
          const correctedValues = getDensityAndVolumeAtBase(
            CommodityGroupCrudeOil,
            obsDensity,
            obsTemp,
            obsPressure,
            quantity
          )

          correctedGravity = correctedValues[0]
            .to(DensityUnitApi)
            .rounded()
            .value
            .toNumber();

          if (typeof bswPercent !== "undefined" && typeof quantity !== "undefined") {
            const correctedQuantity = correctedValues[1]
            if (typeof correctedQuantity !== "undefined") {
              const bswPercentDecimal = divide(bswPercent, bignumber("100"))
              const netQuantityBn = subtract(
                correctedQuantity,
                multiply(
                  correctedQuantity,
                  bswPercentDecimal
                )
              ) as BigNumber
              netQuantity = netQuantityBn.toNumber()
            }
          }
        } catch (error) {
          console.debug(error)
        }
      }

      form.setFieldValue("correctedGravity", correctedGravity)
      form.setFieldValue("netQuantity", netQuantity)
    }

    previousValues.current = values
  }, [form, form.values]);

  return {
    form: form,
    previousValues: previousValues,
  }
}
